import { Target, Brush, MessageSquare, Wrench, Award, Clock, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const icons = [Target, Brush, MessageSquare, Wrench, Award, Clock];

const WhyUs = () => {
  const { t } = useLanguage();
  const points = t.why.points;

  return (
    <section className="relative py-20 md:py-28" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[10px] tracking-[0.3em] uppercase font-display font-bold text-white/70">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e10600]" />
              {t.why.eyebrow}
            </div>
            <h2 className="mt-4 font-display font-black text-3xl md:text-5xl lg:text-6xl tracking-[-0.03em] max-w-2xl">
              {t.why.title}
            </h2>
          </div>
        </div>

        {/* Bento layout: hero card spans 2 cols, then 5 smaller cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Hero "Since 2005" card */}
          <div
            className="relative md:col-span-2 lg:row-span-2 rounded-2xl border border-white/10 bg-[#101010] p-7 md:p-9 overflow-hidden flex flex-col justify-between min-h-[360px]"
            data-testid="why-card-hero"
          >
            <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#e10600]/15 blur-3xl pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-1 bg-[#e10600]" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#e10600]/30 bg-[#e10600]/5 text-[10px] tracking-[0.3em] uppercase font-display font-bold text-[#e10600] mb-6">
                <ShieldCheck className="w-3 h-3" />
                {t.why.heritageLabel}
              </div>
              <p className="font-display font-black text-white leading-[0.9] tracking-[-0.04em] text-[64px] md:text-[112px] tabular-nums">
                20<span className="text-[#e10600]">+</span>
              </p>
              <p className="mt-2 font-display font-bold text-white text-xl md:text-2xl tracking-tight">
                {t.why.heritageHeadline}
              </p>
              <p className="mt-3 text-white/60 leading-relaxed max-w-md">
                {t.why.heritageCopy}
              </p>
            </div>

            <div className="relative flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              <span className="text-[10px] tracking-[0.3em] uppercase font-display font-bold text-white/50">
                {t.why.estLabel}
              </span>
              <span className="font-display font-black text-white text-2xl tabular-nums">
                2005
              </span>
            </div>
          </div>

          {/* 5 smaller cards (skip the duplicated heritage point at index 5) */}
          {points.slice(0, 5).map((p, i) => {
            const Icon = icons[i];
            return (
              <div
                key={p}
                className="group relative rounded-2xl border border-white/10 bg-[#111] p-6 md:p-7 hover:border-[#e10600]/40 transition-colors duration-300"
                data-testid={`why-card-${i}`}
              >
                {/* corner red accent */}
                <span className="pointer-events-none absolute top-0 end-0 w-10 h-10">
                  <span className="absolute top-3 end-0 h-px w-7 bg-[#e10600]/40 group-hover:bg-[#e10600] transition" />
                  <span className="absolute top-0 end-3 w-px h-7 bg-[#e10600]/40 group-hover:bg-[#e10600] transition" />
                </span>

                <div className="w-11 h-11 rounded-xl bg-[#e10600]/10 border border-[#e10600]/30 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#e10600]" />
                </div>
                <h3 className="font-display font-bold text-base md:text-lg text-white tracking-tight">
                  {p.t}
                </h3>
                <p className="mt-2 text-white/60 text-sm leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
